'use client'

import Link from 'next/link'
import { useActionState } from 'react'
import { useFormStatus } from 'react-dom'
import { CONTACT_EMAIL } from '../company'
import { Callout } from '../ui/Callout'
import { ctaClassName, INLINE_LINK } from '../ui/cta'
import { Field } from '../ui/Field'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { submitEnquiry } from './enquiry-action'
import {
  type EnquirySource,
  HONEYPOT_FIELD,
  WORK_KIND_LABELS,
  WORK_KINDS,
  type WorkKind,
} from './enquiry-fields'
import { EMPTY_VALUES, INITIAL_ENQUIRY_STATE } from './enquiry-state'

/** The address, as a link, for every message that offers it as the way round the form. */
function EmailUs() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className={INLINE_LINK}>
      {CONTACT_EMAIL}
    </a>
  )
}

/**
 * Disabled and relabelled while the submit is in flight.
 *
 * Its own component because `useFormStatus` reports the *enclosing* form; called in the
 * parent it would read no form at all. A raw `<button>` with the page's pill class, so
 * it matches the other calls to action on the marketing pages exactly.
 */
function SubmitButton() {
  const { pending } = useFormStatus()

  return (
    <button
      type="submit"
      disabled={pending}
      className={ctaClassName({ size: 'lg' })}
    >
      {pending ? 'Sending…' : 'Send'}
    </button>
  )
}

export interface EnquiryFormProps {
  /** Where the form is: it goes into the internal email, and it sets the layout. */
  readonly source: EnquirySource
  /** Preselects who the visitor is, from `/contact?topic=`. */
  readonly topic?: WorkKind
  /**
   * The "Or email us at …" line beside the button. On by default; the contact page
   * turns it off because it prints the address under the form instead.
   */
  readonly emailFallback?: boolean
}

/**
 * The enquiry form, on the homepage's closing band and on `/contact`.
 *
 * **It works before any JavaScript has run.** The `action` is a Server Action, so with
 * no script the browser posts the form, Next runs the action on the server and renders
 * the page again with the returned state; with script, `useActionState` does the same
 * without a navigation. Either way the state is the only source of what the form shows.
 *
 * **The values come back from the state, not the DOM.** React resets a form once its
 * action settles, so a validation failure would blank every field if they were left
 * uncontrolled. Every `defaultValue` is bound to the returned values instead, which
 * survives the reset and is also what the no-script render needs.
 *
 * **The honeypot** is a real field in a wrapper no person sees or tabs to. A form-filling
 * bot completes it; the action then reports success and sends nothing. `aria-hidden`
 * plus `tabIndex={-1}` keeps it out of the accessibility tree and the tab order, which is
 * what a screen-reader user needs and what axe checks for.
 *
 * **Consent is a box, not a footnote.** The browser's `required` stops the submit until
 * it is ticked; the action checks it again, and the backend records it in the internal
 * email, so the inbox holds the evidence next to the details it covers.
 */
export function EnquiryForm({
  source,
  topic,
  emailFallback = true,
}: EnquiryFormProps) {
  const [state, formAction] = useActionState(
    submitEnquiry,
    INITIAL_ENQUIRY_STATE,
  )

  if (state.status === 'sent') {
    return (
      <Callout tone="success" title="Thanks. We have it.">
        <p>
          Your reference is{' '}
          <strong className="font-mono">{state.reference}</strong>. We have sent
          a short receipt to your email address and will be in touch.
        </p>
      </Callout>
    )
  }

  const values =
    state.status === 'invalid' || state.status === 'failed'
      ? state.values
      : EMPTY_VALUES
  const errors = state.status === 'invalid' ? state.errors : {}
  const id = (field: string) => `enquiry-${source}-${field}`
  const kind = values.kind || topic || ''
  const kindErrorId = errors.kind ? `${id('kind')}-error` : undefined
  const consentErrorId = errors.consent ? `${id('consent')}-error` : undefined

  return (
    <form action={formAction} className="relative flex flex-col gap-5">
      <input type="hidden" name="source" value={source} />

      <div
        aria-hidden="true"
        className="absolute top-0 -left-[10000px] h-px w-px overflow-hidden"
      >
        <label htmlFor={id('company-ref')}>Leave this field empty</label>
        <input
          id={id('company-ref')}
          name={HONEYPOT_FIELD}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      {state.status === 'invalid' ? (
        <Callout tone="danger">Check the fields marked below.</Callout>
      ) : null}
      {state.status === 'failed' && state.reason === 'unavailable' ? (
        <Callout tone="danger" title="We couldn’t send that.">
          Something went wrong at our end. Email us at <EmailUs /> and we will
          pick it up from there.
        </Callout>
      ) : null}
      {state.status === 'failed' && state.reason === 'rate-limited' ? (
        <Callout tone="danger" title="Please try again later.">
          We have had several messages from this connection in the last hour.
          Try again in a while, or email us at <EmailUs />.
        </Callout>
      ) : null}

      {/*
        Two columns from `sm` up: the names share a row, then email and company; the
        select, the message and the consent line each take a row of their own.
      */}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          id={id('first-name')}
          name="firstName"
          label="First name*"
          autoComplete="given-name"
          placeholder="Alex"
          required
          maxLength={60}
          defaultValue={values.firstName}
          error={errors.firstName}
        />
        <Field
          id={id('last-name')}
          name="lastName"
          label="Last name*"
          autoComplete="family-name"
          placeholder="Smith"
          required
          maxLength={60}
          defaultValue={values.lastName}
          error={errors.lastName}
        />
        <Field
          id={id('email')}
          name="email"
          type="email"
          label="Work email*"
          autoComplete="email"
          inputMode="email"
          placeholder="alex@company.co.uk"
          required
          maxLength={254}
          defaultValue={values.email}
          error={errors.email}
        />
        <Field
          id={id('company')}
          name="company"
          label="Company (Optional)"
          autoComplete="organization"
          placeholder="Company name"
          maxLength={120}
          defaultValue={values.company}
          error={errors.company}
        />
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label htmlFor={id('kind')} className="text-sm font-medium text-ink">
            You are*
          </label>
          {/*
            Keyed on the value. React honours a `<select>`'s `defaultValue` only when it
            mounts (an input's is re-applied on every render), so after the post-action
            reset the select would fall back to the placeholder while every other field
            kept what was typed. A new key remounts it with the value the action returned.
          */}
          <Select
            key={kind}
            id={id('kind')}
            name="kind"
            required
            defaultValue={kind}
            aria-describedby={kindErrorId}
            aria-invalid={errors.kind ? true : undefined}
            className={errors.kind ? 'border-danger' : undefined}
          >
            <option value="" disabled>
              Choose one
            </option>
            {WORK_KINDS.map((option) => (
              <option key={option} value={option}>
                {WORK_KIND_LABELS[option]}
              </option>
            ))}
          </Select>
          {errors.kind ? (
            <p id={kindErrorId} className="text-sm text-danger">
              {errors.kind}
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-2">
          <Textarea
            id={id('message')}
            name="message"
            label="Your message*"
            placeholder="For example: we refer around 200 motor claims a month and want them routed and tracked in one place."
            // Under the field, wired to `aria-describedby` by `Textarea`. A hint rather
            // than a line under the form, because it is about this box.
            hint="Describe your operation without including customer data."
            required
            minLength={20}
            maxLength={3000}
            defaultValue={values.message}
            error={errors.message}
            className={source === 'wysehub-homepage' ? 'min-h-32' : 'min-h-56'}
          />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <div className="flex items-start gap-3">
            <input
              id={id('consent')}
              name="consent"
              type="checkbox"
              required
              defaultChecked={values.consent === 'on'}
              aria-describedby={consentErrorId}
              aria-invalid={errors.consent ? true : undefined}
              className="mt-1 size-4 shrink-0 accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            />
            <label
              htmlFor={id('consent')}
              className="text-sm leading-relaxed text-ink"
            >
              I agree to WyseHub using these details to reply to my enquiry, as
              set out in the{' '}
              <Link href="/privacy#accounts" className={INLINE_LINK}>
                privacy notice
              </Link>
              .
            </label>
          </div>
          {errors.consent ? (
            <p id={consentErrorId} className="text-sm text-danger">
              {errors.consent}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <SubmitButton />
        {emailFallback ? (
          <p className="text-sm text-ink-muted">
            Or email us at <EmailUs />.
          </p>
        ) : null}
      </div>
    </form>
  )
}
